import React, { useEffect, useRef } from 'react';
import { DupeLocation } from '../services/locations.service';
import { useAltHotkeys } from '../hooks/useAltHotkeys';

interface DuplicateLocationsModalProps {
  isOpen: boolean;
  locations: DupeLocation[];
  onCancel: () => void;
  onEditExisting: (loc: DupeLocation) => void;
  onAddNew: () => void;
}

const fmtLastUsed = (v: string | null): string => {
  if (!v) return '00/00/0000 00:00';
  const d = new Date(v.replace(' ', 'T'));
  if (isNaN(d.getTime())) return v;
  return d.toLocaleString('en-US', {
    month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

/**
 * Duplicate-locations popup shown before creating a new location, when an
 * existing record matches the looked-up address. Implemented as a native
 * <dialog> opened with showModal() so it renders in the top layer ABOVE the
 * create drawer's dialog (a plain overlay would fall behind it, and a fixed
 * overlay would be trapped by the drawer panel's CSS transform).
 */
export const DuplicateLocationsModal: React.FC<DuplicateLocationsModalProps> = ({
  isOpen,
  locations,
  onCancel,
  onEditExisting,
  onAddNew,
}) => {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (isOpen && !d.open) d.showModal();
    else if (!isOpen && d.open) d.close();
  }, [isOpen]);

  // Native <dialog>, so it stays mounted — hence `layer: isOpen`. Alt+C dismisses.
  useAltHotkeys({ C: onCancel }, undefined, { layer: isOpen });

  return (
    <dialog
      ref={ref}
      // Require an explicit Cancel / Add New choice: swallow Esc, and don't treat
      // a backdrop click as cancel (too easy to do accidentally).
      onCancel={(e) => e.preventDefault()}
      className="p-0 bg-transparent max-w-none max-h-none [&::backdrop]:bg-scrim/40"
    >
      <div className="w-[min(56rem,92vw)] rounded-lg bg-panel shadow-2xl border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-semibold text-center text-body">Duplicate Locations</h2>
        </div>

        <div className="px-4 py-3 max-h-[60vh] overflow-auto">
          <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
            A location with this address already exists. Edit an existing record, or add a new one anyway.
          </p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted border-b border-gray-200 dark:border-gray-700">
                <th className="py-1.5 pr-2 font-medium">Recno</th>
                <th className="py-1.5 px-2 font-medium">Name</th>
                <th className="py-1.5 px-2 font-medium">Address</th>
                <th className="py-1.5 px-2 font-medium">City</th>
                <th className="py-1.5 px-2 font-medium">State</th>
                <th className="py-1.5 px-2 font-medium">Zip</th>
                <th className="py-1.5 px-2 font-medium">Last Used</th>
                <th className="py-1.5 px-2 font-medium text-center">Deleted</th>
                <th className="py-1.5 pl-2 font-medium">Type</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((loc) => (
                <tr key={loc.id} className="border-b border-gray-100 dark:border-gray-700/60">
                  <td className="py-1.5 pr-2 text-label">{loc.id}</td>
                  <td className="py-1.5 px-2">
                    <button
                      type="button"
                      onClick={() => onEditExisting(loc)}
                      className="font-medium text-blue-600 dark:text-blue-400 hover:underline"
                      title="Edit this location"
                    >
                      {loc.lname}
                    </button>
                  </td>
                  <td className="py-1.5 px-2 text-label">{loc.ladd}</td>
                  <td className="py-1.5 px-2 text-label">{loc.lcity}</td>
                  <td className="py-1.5 px-2 text-label">{loc.lstate}</td>
                  <td className="py-1.5 px-2 text-label">{loc.lzip}</td>
                  <td className="py-1.5 px-2 text-label whitespace-nowrap">{fmtLastUsed(loc.lastuseddatetime)}</td>
                  <td className="py-1.5 px-2 text-center">
                    {loc.deleted === 'Y' ? (
                      <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded bg-red-600 text-white">Y</span>
                    ) : (
                      <span className="text-gray-500">N</span>
                    )}
                  </td>
                  <td className="py-1.5 pl-2 text-label">{loc.delivery_type || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={onAddNew}
            className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover"
          >
            Add New
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default DuplicateLocationsModal;
